import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Library from "@/pages/Library";
import Upload from "@/pages/Upload";
import PhotoDetail from "@/pages/PhotoDetail";
import MapView from "@/pages/MapView";
import AtlasList from "@/pages/AtlasList";
import AtlasMap from "@/pages/AtlasMap";
import PublicAtlas from "@/pages/PublicAtlas";
import EntityPage from "@/pages/EntityPage";
import PlacePage from "@/pages/PlacePage";
import LocationsHub from "@/pages/LocationsHub";
import EventSets from "@/pages/EventSets";
import EventPage from "@/pages/EventPage";
import ExploreHome from "@/pages/ExploreHome";
import EmbedContext from "@/pages/EmbedContext";
import Observations from "@/pages/Observations";
import SharedAlbums from "@/pages/SharedAlbums";
import SharedAlbumDetail from "@/pages/SharedAlbumDetail";
import PublicAlbum from "@/pages/PublicAlbum";
import JoinAlbum from "@/pages/JoinAlbum";
import Timeline from "@/pages/Timeline";
import Trips from "@/pages/Trips";
import Stories from "@/pages/Stories";
import StoryDetail from "@/pages/StoryDetail";
import NatureGuide from "@/pages/NatureGuide";
import Photographers from "@/pages/Photographers";
import PhotographerDetail from "@/pages/PhotographerDetail";
import Profile from "@/pages/Profile";
import MySubmissions from "@/pages/MySubmissions";
import GuidedUpload from "@/pages/GuidedUpload";
import SelectiveImport from "@/pages/SelectiveImport";
import ImportedReview from "@/pages/ImportedReview";
import BulkIngest from "@/pages/BulkIngest";
import PublicGallery from "@/pages/PublicGallery";
import PublicPhoto from "@/pages/PublicPhoto";
import EventUpload from "@/pages/EventUpload";
import EventAlbums from "@/pages/EventAlbums";
import NaturePulse from "@/pages/NaturePulse";
import SpeciesPage from "@/pages/SpeciesPage";
import Faq from "@/pages/Faq";
import About from "@/pages/About";
import PublicMap from "@/pages/PublicMap";
import PhotographerPage from "@/pages/PhotographerPage";
import Article from "@/pages/Article";
import Review from "@/pages/Review";
import Search from "@/pages/Search";
import ApiDocs from "@/pages/ApiDocs";
import EmbedStudio from "@/pages/EmbedStudio";
import WebhookConsole from "@/pages/WebhookConsole";
import Inbox from "@/pages/Inbox";
import PublicStory from "@/pages/PublicStory";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import { Planet } from "@phosphor-icons/react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="grid min-h-screen place-items-center bg-[#FAFAFA]">
        <Planet size={40} weight="fill" className="animate-pulse text-[#F27A54]" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

// Home: Unsplash-style discovery homepage for everyone (visitors AND signed-in users).
function Home() {
  return <ExploreHome />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/s/:id" element={<PublicStory />} />
          <Route path="/explore" element={<PublicGallery />} />
          <Route path="/explore/map" element={<PublicMap />} />
          <Route path="/photographer/:key" element={<PhotographerPage />} />
          <Route path="/why-photopulse" element={<Article />} />
          <Route path="/nature-pulse" element={<NaturePulse />} />
          <Route path="/species/:name" element={<SpeciesPage />} />
          <Route path="/p/:id" element={<PublicPhoto />} />
          <Route path="/photo/:id" element={<PublicPhoto />} />
          <Route path="/atlas/p/:slug" element={<PublicAtlas />} />
          <Route path="/e/:type/:slug" element={<EntityPage />} />
          <Route path="/place/:type/:name" element={<PlacePage />} />
          <Route path="/photos" element={<LocationsHub />} />
          <Route path="/photos/:country" element={<LocationsHub />} />
          <Route path="/photos/:country/:state" element={<LocationsHub />} />
          <Route path="/photos/:country/:state/:city" element={<LocationsHub />} />
          <Route path="/event/:id" element={<EventPage />} />
          <Route path="/embed/context" element={<EmbedContext />} />
          <Route path="/albums/p/:id" element={<PublicAlbum />} />
          <Route path="/join/:token" element={<JoinAlbum />} />
          <Route path="/a/:code" element={<EventUpload />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/about" element={<About />} />
          <Route path="/" element={<Home />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/library" element={<Protected><Library /></Protected>} />
          <Route path="/upload" element={<Protected><Upload /></Protected>} />
          <Route path="/upload/guided" element={<Protected><GuidedUpload /></Protected>} />
          <Route path="/upload/photos" element={<Protected><SelectiveImport /></Protected>} />
          <Route path="/upload/imported" element={<Protected><ImportedReview /></Protected>} />
          <Route path="/events" element={<Protected><EventAlbums /></Protected>} />
          <Route path="/event-sets" element={<Protected><EventSets /></Protected>} />
          <Route path="/ingest" element={<Protected><BulkIngest /></Protected>} />
          <Route path="/photos/:id" element={<Protected><PhotoDetail /></Protected>} />
          <Route path="/map" element={<Protected><MapView /></Protected>} />
          <Route path="/atlas" element={<Protected><AtlasList /></Protected>} />
          <Route path="/atlas/:id" element={<Protected><AtlasMap /></Protected>} />
          <Route path="/timeline" element={<Protected><Timeline /></Protected>} />
          <Route path="/trips" element={<Protected><Trips /></Protected>} />
          <Route path="/stories" element={<Protected><Stories /></Protected>} />
          <Route path="/stories/:id" element={<Protected><StoryDetail /></Protected>} />
          <Route path="/nature" element={<Protected><NatureGuide /></Protected>} />
          <Route path="/observations" element={<Protected><Observations /></Protected>} />
          <Route path="/albums" element={<Protected><SharedAlbums /></Protected>} />
          <Route path="/albums/:id" element={<Protected><SharedAlbumDetail /></Protected>} />
          <Route path="/photographers" element={<Protected><Photographers /></Protected>} />
          <Route path="/photographers/:name" element={<Protected><PhotographerDetail /></Protected>} />
          <Route path="/my-uploads" element={<Protected><MySubmissions /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
          <Route path="/review" element={<Protected><Review /></Protected>} />
          <Route path="/search" element={<Protected><Search /></Protected>} />
          <Route path="/api" element={<Protected><ApiDocs /></Protected>} />
          <Route path="/embeds" element={<Protected><EmbedStudio /></Protected>} />
          <Route path="/webhooks" element={<Protected><WebhookConsole /></Protected>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
